import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Gift, MessageCircle, Minus, Plus, Leaf, Coffee, HeartHandshake, Mail, MessageSquareText } from "lucide-react";
import InternalNav from "@/components/InternalNav";
import { useContent } from "@/lib/ContentProvider";
import { api } from "@/lib/api";

/**
 * Page "Bon cadeau" — recréation en pleine page de la modal "Offrir un bon cadeau"
 * de https://refuge-tarifs.emergent.host
 *
 * L'utilisateur compose le séjour à offrir (comme le simulateur /tarifs simplifié),
 * puis renseigne les champs personnels (De la part / Pour / Occasion),
 * puis envoie la demande via WhatsApp.
 */
export default function BonCadeau() {
  const { pricing: PRICING, contact: CONTACT } = useContent();
  // Composeur de séjour
  const [type, setType] = useState("semaine"); // semaine | weekend
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [bain, setBain] = useState(false);
  const [petitDej, setPetitDej] = useState(false);
  const [deco, setDeco] = useState(false);

  // Champs personnels
  const [deLaPart, setDeLaPart] = useState("");
  const [pour, setPour] = useState("");
  const [occasion, setOccasion] = useState("");

  const minNights = type === "weekend" ? PRICING.weekendNights : 1;
  const effectiveNights = Math.max(nights, minNights);
  const travelers = adults + children; // total d'occupants
  const remainingSlots = Math.max(0, PRICING.maxPeople - travelers);
  const TAX_RATE = PRICING.cityTax?.rate ?? 0.05;
  const TAX_CAP = PRICING.cityTax?.perPersonPerNightCap ?? 5.0;
  const fmt = (v) => (Math.round(v * 100) / 100).toFixed(2).replace(".", ",");

  // Ventilation du montant — logique Lodgify / TCO
  const breakdown = useMemo(() => {
    // Hébergement (extraPerson à partir de basePeople — tous voyageurs confondus)
    const nightsPart = PRICING.baseNight * effectiveNights;
    const extraPart = travelers > PRICING.basePeople
      ? PRICING.extraPerson * (travelers - PRICING.basePeople) * effectiveNights
      : 0;
    const housing = nightsPart + extraPart;
    // Petit-déjeuner : UN SEUL service à 25€ × occupants (jamais × nuits)
    const bainPart = bain ? PRICING.options.bainDeFleurs : 0;
    const petitDejPart = petitDej ? PRICING.options.petitDejeuner * travelers : 0;
    const decoPart = deco ? PRICING.options.decors : 0;
    const optionsPart = bainPart + petitDejPart + decoPart;
    // Taxe TCO : (hébergement ÷ nuits ÷ occupants) × 5% plafonné × adultes × nuits
    // Extras EXCLUS. Enfants dans le diviseur mais exonérés.
    const pricePerOccupantPerNight = travelers > 0 && effectiveNights > 0 ? housing / effectiveNights / travelers : 0;
    const taxUnit = Math.min(pricePerOccupantPerNight * TAX_RATE, TAX_CAP);
    const cityTaxPart = taxUnit * adults * effectiveNights;
    const subtotal = housing + optionsPart;
    return {
      nightsPart, extraPart, housing,
      bainPart, petitDejPart, decoPart, optionsPart,
      cityTaxPart, subtotal,
      total: subtotal + cityTaxPart,
    };
  }, [effectiveNights, travelers, adults, bain, petitDej, deco, TAX_RATE, TAX_CAP]);

  const total = breakdown.total;

  const summaryLines = useMemo(() => {
    const lines = [
      `${effectiveNights} nuit${effectiveNights > 1 ? "s" : ""} au refuge · ${adults} adulte${adults > 1 ? "s" : ""}${children > 0 ? ` + ${children} enfant${children > 1 ? "s" : ""}` : ""}`,
    ];
    if (bain) lines.push("Bain de fleurs");
    if (petitDej) lines.push(`Petit déjeuner (un service · ${travelers} pers.)`);
    if (deco) lines.push("Décors du refuge");
    return lines;
  }, [effectiveNights, travelers, adults, children, bain, petitDej, deco]);

  const canSend = deLaPart.trim() && pour.trim();
  const [sending, setSending] = useState(false);
  // Confirmation view state after successful submission
  const [confirmation, setConfirmation] = useState(null); // { code, waUrl } | null

  // Scroll to top when entering confirmation view
  useEffect(() => {
    if (confirmation) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [confirmation]);

  const waTemplate = useMemo(() => {
    // Message pré-rempli (WhatsApp / SMS) — récap ligne par ligne
    const parts = [
      `Bonjour, je souhaite offrir un bon cadeau Curieux Refuge.`,
      `De la part de : ${deLaPart || "—"}`,
      `Pour : ${pour || "—"}`,
      `Occasion : ${occasion || "non précisée"}`,
      ``,
      `Séjour à offrir :`,
      `• Type : ${type === "weekend" ? "Week-end" : "Semaine"}`,
      `• ${summaryLines.join(" · ")}`,
      ``,
      `Détail estimé :`,
      `• Hébergement : ${fmt(breakdown.housing)} €`,
    ];
    if (bain) parts.push(`• Bain fleuri : ${fmt(breakdown.bainPart)} €`);
    if (petitDej) parts.push(`• Petit-déjeuner : ${fmt(breakdown.petitDejPart)} €`);
    if (deco) parts.push(`• Décor du refuge : ${fmt(breakdown.decoPart)} €`);
    parts.push(`• Taxe de séjour : ${fmt(breakdown.cityTaxPart)} €`);
    parts.push(`• Total estimé : ${fmt(total)} €`);
    return parts.join("\n");
  }, [deLaPart, pour, occasion, type, summaryLines, breakdown, bain, petitDej, deco, total]);

  /**
   * Envoi : crée la fiche côté serveur (statut "en attente"),
   * puis redirige vers la messagerie choisie (WhatsApp ou SMS) avec le message pré-rempli.
   * Le demandeur envoie lui-même le message final.
   *
   * Pour préserver le geste utilisateur (nécessaire sur mobile pour éviter le blocage
   * des popups), on ouvre immédiatement un onglet vide (WhatsApp) ou on garde la même
   * fenêtre (SMS) puis on met à jour l'URL une fois le code généré.
   */
  const handleSend = async (channel /* "whatsapp" | "sms" */) => {
    if (!canSend || sending) return;
    setSending(true);

    // 1) Ouvrir immédiatement pour préserver le geste utilisateur (anti popup-blocker)
    let waWin = null;
    if (channel === "whatsapp") {
      waWin = window.open("about:blank", "_blank");
    }

    let code = null;
    try {
      const [dpFirst, ...dpRest] = deLaPart.trim().split(/\s+/);
      const [beFirst, ...beRest] = pour.trim().split(/\s+/);
      const payload = {
        offerantFirstName: dpFirst,
        offerantLastName: dpRest.join(" "),
        beneficiaryFirstName: beFirst,
        beneficiaryLastName: beRest.join(" "),
        occasion: occasion.trim(),
        formule: type,
        nights: effectiveNights,
        travelers,
        options: { bain, petitDej, deco },
        estimatedAmount: breakdown.subtotal,
        cityTax: breakdown.cityTaxPart,
      };
      const res = await api.createGiftCard(payload);
      code = res?.code;
    } catch (_err) {
      // silencieux : on continue vers la messagerie sans le code
    } finally {
      const suffix = code ? `\n\nCode généré : ${code}` : "";
      const fullText = waTemplate + suffix;
      const phone = CONTACT.whatsappNumber || "";
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(fullText)}`;
      // SMS scheme : compatible iOS & Android modernes
      const smsUrl = `sms:+${phone}?&body=${encodeURIComponent(fullText)}`;

      if (channel === "whatsapp") {
        if (waWin && !waWin.closed) {
          try { waWin.location.href = waUrl; } catch (_) {
            window.open(waUrl, "_blank", "noopener,noreferrer");
          }
        } else {
          window.open(waUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        // SMS : redirection sur la même fenêtre → l'OS ouvre l'app Messages
        try { window.location.href = smsUrl; } catch (_) {}
      }

      setSending(false);
      // Bascule vers la page "demande prise en compte"
      setConfirmation({ code, waUrl, smsUrl, channel });
    }
  };

  // ---- VUE CONFIRMATION (après envoi de la demande) ----
  if (confirmation) {
    return (
      <div data-testid="bon-cadeau-confirmation-page" className="bg-refuge-bg min-h-screen">
        <InternalNav />
        <section className="pt-32 md:pt-40 pb-24 md:pb-32 bg-refuge-bg">
          <div className="max-w-2xl mx-auto px-6">
            <div
              data-testid="confirmation-card"
              className="text-center rounded-3xl px-6 py-14 md:px-12 md:py-20 shadow-[0_20px_60px_-20px_rgba(139,37,48,0.15)]"
              style={{ background: "linear-gradient(180deg,#FBF6EE 0%,#F6EDDF 100%)", border: "1px solid rgba(139,37,48,0.12)" }}
            >
              <div
                className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full mb-8 border"
                style={{ borderColor: "rgba(139, 37, 48, 0.25)", background: "rgba(232, 184, 189, 0.18)" }}
              >
                <Mail className="w-9 h-9 md:w-11 md:h-11 text-refuge-ruby" strokeWidth={1.1} />
              </div>
              <p className="font-body text-xs tracking-[0.28em] uppercase text-refuge-ruby mb-5">Curieux Refuge</p>
              <h1 className="font-heading text-3xl md:text-5xl font-light text-refuge-text mb-6 leading-[1.1]">
                Votre demande <span className="italic">a été prise en compte</span>
              </h1>
              <div className="w-16 h-px mx-auto mb-8" style={{ background: "linear-gradient(90deg, transparent, rgba(139,37,48,0.4), transparent)" }} />
              <p className="font-heading italic font-light text-refuge-soft text-lg md:text-xl leading-relaxed mb-3">
                Merci pour cette pensée délicate.
              </p>
              <p className="font-body text-refuge-text/80 text-base md:text-lg leading-relaxed max-w-lg mx-auto mb-10">
                Nous transmettons le récapitulatif au Curieux Refuge par WhatsApp.<br />
                Nous reviendrons vers vous très rapidement pour finaliser votre bon cadeau.
              </p>

              {/* Boutons de secours : WhatsApp + SMS */}
              <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 max-w-lg mx-auto">
                <a
                  data-testid="confirmation-whatsapp-fallback"
                  href={confirmation.waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-body text-sm tracking-wide transition-transform hover:-translate-y-0.5"
                  style={{ background: "linear-gradient(180deg,#25D366 0%,#128C7E 100%)", boxShadow: "0 10px 25px -10px rgba(18,140,126,0.55)" }}
                >
                  <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                  Envoyer sur WhatsApp
                </a>
                <a
                  data-testid="confirmation-sms-fallback"
                  href={confirmation.smsUrl}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-body text-sm tracking-wide transition-transform hover:-translate-y-0.5 bg-refuge-ruby hover:bg-refuge-ruby/90"
                >
                  <MessageSquareText className="w-5 h-5" strokeWidth={1.5} />
                  Envoyer par SMS
                </a>
              </div>
              <p className="font-body text-xs text-refuge-text/55 mt-4">
                Choisissez la messagerie qui vous convient — le message est déjà pré-rempli, il vous suffit d'appuyer sur envoyer.
              </p>

              {confirmation.code && (
                <div className="mt-12 pt-8" style={{ borderTop: "1px solid rgba(139,37,48,0.12)" }}>
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby/80 mb-2">Référence de votre demande</p>
                  <p data-testid="confirmation-code" className="font-heading text-xl md:text-2xl text-refuge-text tracking-wider">{confirmation.code}</p>
                  <p className="font-body text-xs text-refuge-text/55 mt-2">Conservez cette référence — elle nous permettra de retrouver rapidement votre demande.</p>
                </div>
              )}

              <div className="mt-12">
                <Link
                  data-testid="confirmation-back-home"
                  to="/accueil"
                  className="inline-flex items-center gap-2 font-body text-sm text-refuge-ruby hover:text-refuge-text transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="bon-cadeau-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      {/* HEADER */}
      <section className="pt-32 md:pt-40 pb-8 md:pb-12 bg-refuge-bg">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full mb-6 border" style={{ borderColor: "rgba(139, 37, 48, 0.25)", background: "rgba(232, 184, 189, 0.15)" }}>
            <Gift className="w-7 h-7 md:w-9 md:h-9 text-refuge-ruby" strokeWidth={1.2} />
          </div>
          <p className="font-body text-xs tracking-[0.24em] uppercase text-refuge-ruby mb-4">Bon cadeau</p>
          <h1 className="font-heading text-4xl md:text-6xl font-light text-refuge-text mb-5 leading-[1.05]">
            Offrir <span className="italic">un moment</span>
          </h1>
          <p className="font-heading italic font-light text-refuge-soft/85 text-lg md:text-xl leading-relaxed">
            Quelques détails pour personnaliser votre demande.
          </p>
        </div>
      </section>

      {/* CARTE PRINCIPALE */}
      <section className="pb-16 md:pb-24 bg-refuge-bg">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div
            className="bg-white rounded-3xl border border-refuge-border p-6 md:p-10"
            style={{ boxShadow: "rgba(45, 40, 37, 0.15) 0px 30px 60px -40px" }}
          >
            {/* Composeur séjour */}
            <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-1">Le séjour à offrir</p>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-refuge-text mb-6 leading-tight">
              Composer <span className="italic">votre cadeau</span>
            </h2>

            {/* Type */}
            <Row label="Type de séjour">
              <Toggle
                value={type}
                onChange={(v) => setType(v)}
                options={[{ id: "semaine", l: "Semaine" }, { id: "weekend", l: "Week-end" }]}
                testid="bc-type"
              />
            </Row>

            {/* Nights */}
            <Row label="Nombre de nuits" sublabel={type === "weekend" ? `minimum ${PRICING.weekendNights}` : "1 à 7"}>
              <Counter
                value={effectiveNights}
                onDec={() => setNights(Math.max(minNights, effectiveNights - 1))}
                onInc={() => setNights(Math.min(7, effectiveNights + 1))}
                decDisabled={effectiveNights <= minNights}
                incDisabled={effectiveNights >= 7}
                testid="bc-nights"
              />
            </Row>

            {/* Travelers — Adultes / Enfants séparés (règle TCO) */}
            <Row label="Adultes" sublabel="assujettis à la taxe de séjour">
              <Counter
                value={adults}
                onDec={() => setAdults(Math.max(1, adults - 1))}
                onInc={() => { if (remainingSlots > 0) setAdults(adults + 1); }}
                decDisabled={adults <= 1}
                incDisabled={remainingSlots === 0}
                testid="bc-adults"
              />
            </Row>
            <Row label="Enfants" sublabel="moins de 18 ans · exonérés de taxe de séjour">
              <Counter
                value={children}
                onDec={() => setChildren(Math.max(0, children - 1))}
                onInc={() => { if (remainingSlots > 0) setChildren(children + 1); }}
                decDisabled={children <= 0}
                incDisabled={remainingSlots === 0}
                testid="bc-children"
              />
            </Row>

            {/* Options */}
            <Row label="Bain de fleurs" sublabel={`rituel sensoriel · +${PRICING.options.bainDeFleurs}€`}>
              <Toggle
                value={bain ? "oui" : "non"}
                onChange={(v) => setBain(v === "oui")}
                options={[{ id: "non", l: "Non" }, { id: "oui", l: "Oui" }]}
                testid="bc-bain"
              />
            </Row>
            <Row label="Petit déjeuner" sublabel={`+${PRICING.options.petitDejeuner}€ / personne · un seul service`}>
              <Toggle
                value={petitDej ? "oui" : "non"}
                onChange={(v) => setPetitDej(v === "oui")}
                options={[{ id: "non", l: "Non" }, { id: "oui", l: "Oui" }]}
                testid="bc-petitdej"
              />
            </Row>
            <Row label="Décors du refuge" sublabel={`mise en scène · +${PRICING.options.decors}€`} isLast>
              <Toggle
                value={deco ? "oui" : "non"}
                onChange={(v) => setDeco(v === "oui")}
                options={[{ id: "non", l: "Non" }, { id: "oui", l: "Oui" }]}
                testid="bc-deco"
              />
            </Row>

            {/* Personnalisation */}
            <div className="mt-8 pt-8 border-t border-refuge-border">
              <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-1">Personnalisation</p>
              <h3 className="font-heading text-xl md:text-2xl font-light text-refuge-text mb-6">
                À qui offrez-vous <span className="italic">ce moment</span> ?
              </h3>

              <Field label="De la part de" testid="bc-de-la-part-field">
                <input
                  type="text"
                  data-testid="bc-de-la-part"
                  value={deLaPart}
                  onChange={(e) => setDeLaPart(e.target.value)}
                  placeholder="Votre prénom"
                  className="w-full font-body text-base text-refuge-text py-3 px-4 rounded-full border border-refuge-border bg-white focus:outline-none focus:border-refuge-ruby transition-colors"
                />
              </Field>
              <Field label="Pour" testid="bc-pour-field">
                <input
                  type="text"
                  data-testid="bc-pour"
                  value={pour}
                  onChange={(e) => setPour(e.target.value)}
                  placeholder="Prénom de la personne qui reçoit"
                  className="w-full font-body text-base text-refuge-text py-3 px-4 rounded-full border border-refuge-border bg-white focus:outline-none focus:border-refuge-ruby transition-colors"
                />
              </Field>
              <Field label="Occasion" testid="bc-occasion-field" isLast>
                <input
                  type="text"
                  data-testid="bc-occasion"
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  placeholder="Anniversaire, Saint-Valentin, surprise…"
                  className="w-full font-body text-base text-refuge-text py-3 px-4 rounded-full border border-refuge-border bg-white focus:outline-none focus:border-refuge-ruby transition-colors"
                />
              </Field>
            </div>

            {/* Comment ça se passe */}
            <div
              className="mt-8 p-5 md:p-6 rounded-2xl border border-refuge-ruby/25"
              style={{ background: "rgba(232, 184, 189, 0.12)" }}
              data-testid="bc-comment-block"
            >
              <p className="font-body text-xs tracking-[0.22em] uppercase text-refuge-ruby mb-4">Comment ça se passe</p>
              <ol className="space-y-3">
                {[
                  "Vous envoyez votre demande par WhatsApp.",
                  "Une fois la demande prise en compte, vous recevez un lien de paiement sécurisé.",
                  "Après paiement, vous recevez votre bon cadeau personnalisé avec un code unique à offrir.",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="font-heading italic text-refuge-ruby text-sm mt-[2px] shrink-0 w-4 text-right">{i + 1}</span>
                    <span className="font-body text-sm text-refuge-text/90 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Récap séjour + estimation détaillée */}
            <div className="mt-8 pt-6 border-t border-refuge-border">
              <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby text-center mb-4">
                Récapitulatif
              </p>

              {/* Lignes de détail — logique Lodgify / TCO */}
              <div className="max-w-md mx-auto mb-5" data-testid="bc-breakdown">
                <BreakdownLine label="Hébergement" value={breakdown.housing} />
                {breakdown.bainPart > 0 && <BreakdownLine label="Bain fleuri" value={breakdown.bainPart} />}
                {breakdown.petitDejPart > 0 && <BreakdownLine label={`Petit-déjeuner (un service · ${travelers} pers.)`} value={breakdown.petitDejPart} />}
                {breakdown.decoPart > 0 && <BreakdownLine label="Décor du refuge" value={breakdown.decoPart} />}
                <BreakdownLine
                  label={`Taxe de séjour · ${adults} adulte${adults > 1 ? "s" : ""} × ${effectiveNights} nuit${effectiveNights > 1 ? "s" : ""}`}
                  value={breakdown.cityTaxPart}
                  subtle
                />
              </div>
              <p className="max-w-md mx-auto font-body italic text-[10px] leading-relaxed text-refuge-soft text-center mb-6 px-4">
                Taxe de séjour 5 % plafonnée à {fmt(TAX_CAP)} € / adulte / nuit · reversée intégralement à la collectivité.
              </p>

              <div
                className="max-w-md mx-auto flex items-baseline justify-between px-4 py-4 rounded-xl"
                style={{
                  background: "rgba(255, 253, 249, 0.7)",
                  border: "1px solid rgba(184, 153, 104, 0.35)",
                }}
              >
                <span className="font-body text-[10px] tracking-[0.28em] uppercase text-refuge-ruby">
                  Total estimé
                </span>
                <span
                  className="font-heading text-refuge-text tracking-tight"
                  data-testid="bc-total"
                  style={{ fontSize: "clamp(1.7rem, 6vw, 2.4rem)" }}
                >
                  {fmt(total)}<span className="text-lg align-top ml-0.5"> €</span>
                </span>
              </div>
            </div>

            {/* Actions — Envoi de la demande (2 canaux au choix) */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSend("whatsapp")}
                disabled={!canSend || sending}
                data-testid="bc-envoyer-whatsapp"
                className={`inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-body text-sm tracking-wide transition-all ${
                  canSend && !sending
                    ? "text-white hover:-translate-y-0.5"
                    : "text-white cursor-not-allowed opacity-60"
                }`}
                style={{
                  background: canSend && !sending
                    ? "linear-gradient(180deg,#25D366 0%,#128C7E 100%)"
                    : "rgba(37, 211, 102, 0.45)",
                  boxShadow: canSend && !sending ? "0 10px 22px -12px rgba(18,140,126,0.55)" : "none",
                }}
              >
                <MessageCircle size={16} strokeWidth={1.5} />
                {sending ? "Création en cours…" : "Envoyer sur WhatsApp"}
              </button>
              <button
                type="button"
                onClick={() => handleSend("sms")}
                disabled={!canSend || sending}
                data-testid="bc-envoyer-sms"
                className={`inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-body text-sm tracking-wide transition-all ${
                  canSend && !sending
                    ? "bg-refuge-ruby text-white hover:bg-refuge-ruby/90 hover:-translate-y-0.5"
                    : "bg-refuge-ruby/40 text-white cursor-not-allowed"
                }`}
              >
                <MessageSquareText size={16} strokeWidth={1.5} />
                {sending ? "Création en cours…" : "Envoyer par SMS"}
              </button>
            </div>

            <div className="mt-4 flex justify-center">
              <Link
                to="/accueil"
                data-testid="bc-annuler"
                className="inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-all"
              >
                Annuler
              </Link>
            </div>

            {!canSend && (
              <p className="mt-4 font-body text-xs italic text-center text-refuge-soft/80">
                Renseignez votre prénom et celui du destinataire pour envoyer votre demande.
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="bg-refuge-bg pb-16 text-center">
        <Link
          to="/accueil"
          data-testid="back-to-hub"
          className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} /> Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}

// ============================================================
// Sous-composants
// ============================================================
function Row({ label, sublabel, children, isLast }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 py-4 ${isLast ? "" : "border-b border-refuge-border/60"}`}>
      <div className="flex flex-col">
        <span className="font-heading text-base md:text-lg text-refuge-text leading-snug">{label}</span>
        {sublabel && <span className="text-xs text-refuge-soft italic font-light mt-0.5">{sublabel}</span>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex items-center p-1 rounded-full border border-refuge-border bg-white" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          data-testid={`${testid}-${o.id}`}
          onClick={() => onChange(o.id)}
          className={`px-4 md:px-5 py-2 rounded-full text-[11px] tracking-[0.15em] uppercase font-medium transition-all ${
            value === o.id ? "bg-refuge-text text-refuge-bg" : "text-refuge-soft hover:text-refuge-text"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function Counter({ value, onDec, onInc, decDisabled, incDisabled, testid }) {
  return (
    <div className="inline-flex items-center border border-refuge-border rounded-full bg-white overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={onDec}
        disabled={decDisabled}
        data-testid={`${testid}-dec`}
        aria-label="diminuer"
        className="w-11 h-11 flex items-center justify-center text-refuge-text hover:text-refuge-ruby transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Minus className="w-4 h-4" strokeWidth={1.4} />
      </button>
      <span className="font-heading text-xl text-refuge-text min-w-[2.5rem] text-center select-none" data-testid={`${testid}-value`}>
        {value}
      </span>
      <button
        type="button"
        onClick={onInc}
        disabled={incDisabled}
        data-testid={`${testid}-inc`}
        aria-label="augmenter"
        className="w-11 h-11 flex items-center justify-center text-refuge-text hover:text-refuge-ruby transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" strokeWidth={1.4} />
      </button>
    </div>
  );
}

function Field({ label, children, isLast, testid }) {
  return (
    <div className={isLast ? "" : "mb-5"} data-testid={testid}>
      <label className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function BreakdownLine({ label, value, subtle }) {
  const formatted = (Math.round(value * 100) / 100).toFixed(2).replace(".", ",");
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-refuge-border/40 last:border-b-0">
      <span
        className={`font-body ${subtle ? "text-xs italic text-refuge-soft" : "text-sm text-refuge-text"}`}
      >
        {label}
      </span>
      <span
        className={`font-body tabular-nums ${subtle ? "text-xs text-refuge-soft" : "text-sm text-refuge-text"}`}
      >
        {formatted} €
      </span>
    </div>
  );
}

